import React, { useState, useEffect, useMemo } from 'react';
import axiosInstance from '../../api/axiosConfig';
import { SECTION_TEMPLATE, CLEARANCE_REASONS } from './clearanceTemplate';

/**
 * Reusable assignee picker: searchable checkbox list of users.
 */
const AssigneePicker = ({ users, selected, onToggle }) => {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState('');

    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        if (!needle) return users;
        return users.filter(u =>
            (u.fullName || '').toLowerCase().includes(needle) ||
            (u.username || '').toLowerCase().includes(needle) ||
            (u.department || '').toLowerCase().includes(needle)
        );
    }, [users, q]);

    const selectedUsers = users.filter(u => selected.includes(u._id));

    return (
        <div className="assignee-picker">
            <div className="assignee-chips" onClick={() => setOpen(o => !o)}>
                {selectedUsers.length === 0 ? (
                    <span className="assignee-placeholder">Click to assign users…</span>
                ) : (
                    selectedUsers.map(u => (
                        <span key={u._id} className="assignee-chip">
                            {u.fullName}
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); onToggle(u._id); }}
                            >×</button>
                        </span>
                    ))
                )}
            </div>
            {open && (
                <div className="assignee-dropdown">
                    <input
                        type="text"
                        className="assignee-search"
                        placeholder="Search users…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        autoFocus
                    />
                    <div className="assignee-options">
                        {filtered.length === 0 ? (
                            <div className="assignee-none">No users</div>
                        ) : filtered.map(u => (
                            <label key={u._id} className="assignee-option">
                                <input
                                    type="checkbox"
                                    checked={selected.includes(u._id)}
                                    onChange={() => onToggle(u._id)}
                                />
                                <span>{u.fullName}</span>
                                {u.department && <span className="assignee-dept">{u.department}</span>}
                            </label>
                        ))}
                    </div>
                    <button type="button" className="assignee-done" onClick={() => setOpen(false)}>
                        Done
                    </button>
                </div>
            )}
        </div>
    );
};

/**
 * Staff name field backed by the employee master.
 *
 * Typing filters the master; picking a row fills code, designation, branch and
 * QID in one go. It stays a plain text field underneath — a person who is not in
 * the master (or is in it with stale details) can still be typed in by hand, and
 * every filled field remains editable afterwards.
 */
const EmployeePicker = ({ employees, value, onType, onPick }) => {
    const [open, setOpen] = useState(false);

    const matches = useMemo(() => {
        const needle = (value || '').trim().toLowerCase();
        const list = needle
            ? employees.filter(e =>
                (e.fullName || '').toLowerCase().includes(needle) ||
                (e.employeeCode || '').toLowerCase().includes(needle))
            : employees;
        return list.slice(0, 50);
    }, [employees, value]);

    return (
        <div className="employee-picker">
            <input
                value={value}
                onChange={(e) => { onType(e.target.value); setOpen(true); }}
                onFocus={() => setOpen(true)}
                onBlur={() => setTimeout(() => setOpen(false), 120)}
                placeholder="Type a name or staff code…"
                autoComplete="off"
                required
            />
            {open && employees.length > 0 && (
                <div className="employee-dropdown">
                    {matches.length === 0 ? (
                        <div className="employee-none">No match — the name will be saved as typed</div>
                    ) : matches.map(emp => (
                        <button
                            type="button"
                            key={emp._id}
                            className="employee-option"
                            onMouseDown={(e) => { e.preventDefault(); onPick(emp); setOpen(false); }}
                        >
                            <span className="employee-option-name">{emp.fullName}</span>
                            <span className="employee-option-meta">
                                {[emp.employeeCode, emp.designation, emp.branch].filter(Boolean).join(' · ')}
                            </span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const emptyEmployee = {
    name: '', staffCode: '', designation: '', branch: '',
    qid: '', qidExpiry: '', reportingTo: '',
    date: new Date().toISOString().slice(0, 10)
};

const CreateClearanceModal = ({ onClose, onCreated }) => {
    const [employee, setEmployee] = useState(emptyEmployee);
    const [reason, setReason] = useState('');
    const [reasonOther, setReasonOther] = useState('');
    const [travelDate, setTravelDate] = useState('');
    const [assignments, setAssignments] = useState({}); // { sectionKey: [userId] }
    const [users, setUsers] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        axiosInstance.get('/api/clearance/users')
            .then(res => { if (res.data?.success) setUsers(res.data.data || []); })
            .catch(() => setError('Failed to load users for assignment'));

        // The master is a convenience, not a requirement — if it fails to load the
        // form still works as free-text entry, so this does not raise an error.
        axiosInstance.get('/api/clearance/employees')
            .then(res => { if (res.data?.success) setEmployees(res.data.data || []); })
            .catch(() => { });
    }, []);

    const setField = (k, v) => setEmployee(prev => ({ ...prev, [k]: v }));

    const pickEmployee = (emp) => setEmployee(prev => ({
        ...prev,
        name: emp.fullName || '',
        staffCode: emp.employeeCode || '',
        designation: emp.designation || '',
        branch: emp.branch || '',
        qid: emp.qid || '',
        qidExpiry: emp.qidExpiry ? String(emp.qidExpiry).slice(0, 10) : ''
    }));

    const toggleAssignee = (sectionKey, userId) => {
        setAssignments(prev => {
            const current = prev[sectionKey] || [];
            const next = current.includes(userId)
                ? current.filter(id => id !== userId)
                : [...current, userId];
            return { ...prev, [sectionKey]: next };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!employee.name.trim()) { setError('Employee name is required'); return; }
        if (!reason) { setError('Please select a clearance reason'); return; }
        if (reason === 'other' && !reasonOther.trim()) { setError('Please specify the other reason'); return; }

        setSaving(true);
        try {
            const payload = {
                employee: {
                    ...employee,
                    qidExpiry: employee.qidExpiry || null,
                    date: employee.date || null
                },
                clearance: { reason, reasonOther, travelDate: travelDate || null },
                assignments
            };
            const res = await axiosInstance.post('/api/clearance', payload);
            if (res.data?.success) onCreated(res.data.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to create clearance request');
            setSaving(false);
        }
    };

    return (
        <div className="clearance-modal-overlay" onClick={onClose}>
            <div className="clearance-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>New Clearance Request</h2>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>

                <form onSubmit={handleSubmit} className="modal-body">
                    {error && <div className="clearance-error">{error}</div>}

                    <h3 className="form-section-title">Employee Details</h3>
                    <p className="form-hint">
                        Start typing a name or staff code to pull the employee from the master —
                        code, designation, branch and QID fill in automatically and stay editable.
                    </p>
                    <div className="form-grid">
                        <label>
                            <span>Staff Name *</span>
                            <EmployeePicker
                                employees={employees}
                                value={employee.name}
                                onType={(v) => setField('name', v)}
                                onPick={pickEmployee}
                            />
                        </label>
                        <label>
                            <span>Staff Code</span>
                            <input value={employee.staffCode} onChange={(e) => setField('staffCode', e.target.value)} />
                        </label>
                        <label>
                            <span>Designation</span>
                            <input value={employee.designation} onChange={(e) => setField('designation', e.target.value)} />
                        </label>
                        <label>
                            <span>Reporting To</span>
                            <input value={employee.reportingTo} onChange={(e) => setField('reportingTo', e.target.value)} />
                        </label>
                        <label>
                            <span>Branch</span>
                            <input value={employee.branch} onChange={(e) => setField('branch', e.target.value)} />
                        </label>
                        <label>
                            <span>Staff QID</span>
                            <input value={employee.qid} onChange={(e) => setField('qid', e.target.value)} />
                        </label>
                        <label>
                            <span>QID Expiry Date</span>
                            <input type="date" value={employee.qidExpiry} onChange={(e) => setField('qidExpiry', e.target.value)} />
                        </label>
                        <label>
                            <span>Date</span>
                            <input type="date" value={employee.date} onChange={(e) => setField('date', e.target.value)} />
                        </label>
                    </div>

                    <h3 className="form-section-title">Clearance Details</h3>
                    <div className="form-grid">
                        <label>
                            <span>Reason for Clearance *</span>
                            <select value={reason} onChange={(e) => setReason(e.target.value)} required>
                                <option value="">Select reason…</option>
                                {CLEARANCE_REASONS.map(r => (
                                    <option key={r.value} value={r.value}>{r.label}</option>
                                ))}
                            </select>
                        </label>
                        {reason === 'other' && (
                            <label>
                                <span>Specify Other</span>
                                <input value={reasonOther} onChange={(e) => setReasonOther(e.target.value)} />
                            </label>
                        )}
                        <label>
                            <span>Travel Date</span>
                            <input type="date" value={travelDate} onChange={(e) => setTravelDate(e.target.value)} />
                        </label>
                    </div>

                    <h3 className="form-section-title">Assign Sections</h3>
                    <p className="form-hint">
                        Pick who fills and signs each section. Only assigned users can edit that section.
                    </p>
                    <div className="assign-list">
                        {SECTION_TEMPLATE.map(section => (
                            <div className="assign-row" key={section.key}>
                                <div className="assign-label">{section.title}</div>
                                <AssigneePicker
                                    users={users}
                                    selected={assignments[section.key] || []}
                                    onToggle={(userId) => toggleAssignee(section.key, userId)}
                                />
                            </div>
                        ))}
                    </div>

                    <div className="modal-footer">
                        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={saving}>
                            {saving ? 'Creating…' : 'Create Request'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateClearanceModal;
